import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  School as SchoolIcon,
  Group as TeamIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PlayArrow as ProcessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  TrendingUp as ProjectionsIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { auth } from '../../../../../firebase';
import { getApiBase } from '../../../../../utils/apiConfig';

/**
 * UpgradeDropAPinProcessor Component
 * 
 * Allows bulk upgrading of drop-a-pin schools to Non-NCES schools
 * by importing enrollment data from Excel files.
 */
const UpgradeDropAPinProcessor = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const [previewData, setPreviewData] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  
  const [selectedSchools, setSelectedSchools] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  
  const [runProjections, setRunProjections] = useState(true);
  const [runMetrics, setRunMetrics] = useState(true);
  
  // Manual matching for unmatched schools: { excelSchoolName: { uuid, name, enrollment_data } }
  const [manualMatches, setManualMatches] = useState({});
  
  // Expanded enrollment preview: which school UUIDs have expanded preview
  const [expandedEnrollment, setExpandedEnrollment] = useState(new Set());

  // Steps for the stepper
  const steps = ['Select Team', 'Upload Excel', 'Preview & Select', 'Process'];

  // Helper: Get auth headers
  const getAuthHeaders = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      console.warn('No authenticated user found');
      return {};
    }
    try {
      const token = await firebaseUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  };

  // Helper: Get API base URL
  // Load available teams
  const loadAvailableTeams = useCallback(async () => {
    setIsLoadingTeams(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/admin/teams?getAllTeams=true`, {
        headers: authHeaders,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const teams = data.teams || [];
        // Deduplicate by ID
        const uniqueTeams = teams.filter((team, index, self) =>
          index === self.findIndex(t => t.id === team.id)
        );
        setAvailableTeams(uniqueTeams);
      } else {
        console.error('Teams API error:', response.status);
        enqueueSnackbar('Failed to load teams', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      enqueueSnackbar('Failed to load teams', { variant: 'error' });
    } finally {
      setIsLoadingTeams(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadAvailableTeams();
  }, [loadAvailableTeams]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      enqueueSnackbar('Please select an Excel file (.xlsx or .xls)', { variant: 'error' });
      return;
    }
    setSelectedFile(file);
    setPreviewData(null);
    setSelectedSchools(new Set());
    enqueueSnackbar(`File selected: ${file.name}`, { variant: 'info' });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // Parse Excel and get preview
  const parseExcelFile = async () => {
    if (!selectedFile || !selectedTeam) {
      enqueueSnackbar('Please select a team and upload a file', { variant: 'error' });
      return;
    }

    setIsParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('team_id', selectedTeam);
      formData.append('min_match_score', '75'); // Lower threshold for better matching

      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/schools/upgrade-drop-a-pins/preview`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        
        // Auto-select all matched schools
        const matchedUuids = new Set(data.matched_schools?.map(s => s.uuid) || []);
        setSelectedSchools(matchedUuids);
        
        const matchCount = data.matched_schools?.length || 0;
        const unmatchedCount = data.unmatched_schools?.length || 0;
        
        if (matchCount > 0) {
          enqueueSnackbar(`Found ${matchCount} matching schools, ${unmatchedCount} unmatched`, { 
            variant: unmatchedCount > 0 ? 'warning' : 'success' 
          });
        } else {
          enqueueSnackbar('No schools could be matched from the Excel file', { variant: 'warning' });
        }
      } else {
        const errorText = await response.text();
        console.error('Preview error:', response.status, errorText);
        enqueueSnackbar(`Failed to parse Excel: ${errorText}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error parsing Excel:', error);
      enqueueSnackbar('Failed to parse Excel file', { variant: 'error' });
    } finally {
      setIsParsing(false);
    }
  };

  // Toggle school selection
  const toggleSchoolSelection = (uuid) => {
    setSelectedSchools(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  // Select/deselect all (includes both auto and manual matches)
  const toggleAllSchools = () => {
    const allMatchedSchools = getAllMatchedSchools();
    if (selectedSchools.size === allMatchedSchools.length) {
      setSelectedSchools(new Set());
    } else {
      setSelectedSchools(new Set(allMatchedSchools.map(s => s.uuid)));
    }
  };

  // Process selected schools
  const processSelectedSchools = async () => {
    if (selectedSchools.size === 0) {
      enqueueSnackbar('Please select at least one school to process', { variant: 'error' });
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare the schools data for processing (auto + manual matches)
      const allSchools = getAllMatchedSchools();
      const schoolsToProcess = allSchools
        .filter(school => selectedSchools.has(school.uuid))
        .map(school => ({
          uuid: school.uuid,
          enrollment_data: school.enrollment_data
        }));

      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/schools/upgrade-drop-a-pins/confirm`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          team_id: selectedTeam,
          schools: schoolsToProcess,
          run_projections: runProjections,
          run_metrics: runMetrics
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProcessingResults(data);
        
        const successCount = data.summary?.successful || 0;
        const failedCount = data.summary?.failed || 0;
        
        if (failedCount === 0) {
          enqueueSnackbar(`Successfully upgraded ${successCount} schools!`, { variant: 'success' });
        } else {
          enqueueSnackbar(`Upgraded ${successCount} schools, ${failedCount} failed`, { variant: 'warning' });
        }
      } else {
        const errorText = await response.text();
        console.error('Processing error:', response.status, errorText);
        enqueueSnackbar(`Processing failed: ${errorText}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error processing schools:', error);
      enqueueSnackbar('Processing failed', { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset the workflow
  const resetWorkflow = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setSelectedSchools(new Set());
    setProcessingResults(null);
    setManualMatches({});
    setExpandedEnrollment(new Set());
  };

  // Get current step based on state
  const getCurrentStep = () => {
    if (processingResults) return 4;
    if (previewData && selectedSchools.size > 0) return 3;
    if (previewData) return 2;
    if (selectedTeam && selectedFile) return 2;
    if (selectedTeam) return 1;
    return 0;
  };

  // Format enrollment data for display
  const formatEnrollmentSummary = (enrollmentData) => {
    if (!enrollmentData) return 'No data';
    const years = Object.keys(enrollmentData).sort();
    const totalYears = years.length;
    const grades = new Set();
    let totalRecords = 0;
    
    years.forEach(year => {
      Object.keys(enrollmentData[year]).forEach(grade => {
        grades.add(grade);
        totalRecords++;
      });
    });
    
    return `${totalYears} years, ${grades.size} grades, ${totalRecords} records`;
  };

  // Get team name by ID (team IDs are strings)
  const getTeamName = (teamId) => {
    const team = availableTeams.find(t => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  // Handle manual match selection for unmatched schools
  const handleManualMatch = (excelSchoolName, selectedUuid, enrollmentData) => {
    if (!selectedUuid) {
      // Clear the match
      setManualMatches(prev => {
        const next = { ...prev };
        delete next[excelSchoolName];
        return next;
      });
      return;
    }
    
    const dropAPin = previewData?.drop_a_pins?.find(d => d.uuid === selectedUuid);
    if (dropAPin) {
      setManualMatches(prev => ({
        ...prev,
        [excelSchoolName]: {
          uuid: selectedUuid,
          name: dropAPin.name,
          matched_name: dropAPin.name,
          match_score: 100, // Manual match
          enrollment_data: enrollmentData,
          is_manual: true
        }
      }));
      // Auto-select the manually matched school
      setSelectedSchools(prev => new Set([...prev, selectedUuid]));
    }
  };

  // Get UUIDs that are already matched (auto or manual) to avoid duplicate selections
  const getAlreadyMatchedUuids = () => {
    const matched = new Set();
    // Add auto-matched UUIDs
    previewData?.matched_schools?.forEach(s => matched.add(s.uuid));
    // Add manually matched UUIDs
    Object.values(manualMatches).forEach(m => matched.add(m.uuid));
    return matched;
  };

  // Get available drop-a-pins for manual matching (excluding already matched)
  const getAvailableDropAPins = (currentExcelSchool) => {
    const alreadyMatched = getAlreadyMatchedUuids();
    // If this school has a manual match, include its UUID as available
    const currentMatch = manualMatches[currentExcelSchool]?.uuid;
    
    return (previewData?.drop_a_pins || []).filter(dap => 
      !alreadyMatched.has(dap.uuid) || dap.uuid === currentMatch
    );
  };

  // Toggle enrollment preview for a school
  const toggleEnrollmentPreview = (uuid) => {
    setExpandedEnrollment(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  // Get all schools to process (auto + manual matches)
  const getAllMatchedSchools = () => {
    const autoMatched = previewData?.matched_schools || [];
    const manuallyMatched = Object.values(manualMatches);
    return [...autoMatched, ...manuallyMatched];
  };

  // Format enrollment data as a table for preview
  const formatEnrollmentTable = (enrollmentData) => {
    if (!enrollmentData) return null;
    
    const years = Object.keys(enrollmentData).sort();
    const allGrades = new Set();
    years.forEach(year => {
      Object.keys(enrollmentData[year]).forEach(grade => allGrades.add(grade));
    });
    const grades = Array.from(allGrades).sort((a, b) => {
      const order = ['PK', 'KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
      return order.indexOf(a) - order.indexOf(b);
    });
    
    return { years, grades, data: enrollmentData };
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <ProjectionsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Upgrade Drop-a-Pin Schools
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Import enrollment data from Excel to convert drop-a-pin locations into full Non-NCES schools 
            with projections capability. Schools are matched by name within the selected team.
          </Typography>

          <Alert severity="info" icon={<InfoIcon />}>
            <AlertTitle>Excel Format Requirements</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>One sheet per school year (e.g., "2017-2018", "2018", "2017-18")</li>
              <li>First column should contain school names</li>
              <li>Grade columns: K, 1, 2, 3, ... 12 (or variations like "Kindergarten", "Grade 1")</li>
              <li>Enrollment numbers in the cells</li>
            </ul>
          </Alert>
        </CardContent>
      </Card>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={getCurrentStep()} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Processing Results */}
      {processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckIcon color="success" sx={{ fontSize: 32 }} />
              <Typography variant="h5" color="success.main">
                Processing Complete!
              </Typography>
            </Box>

            <Alert 
              severity={processingResults.summary?.failed === 0 ? 'success' : 'warning'} 
              sx={{ mb: 2 }}
            >
              <AlertTitle>Summary</AlertTitle>
              Successfully upgraded <strong>{processingResults.summary?.successful || 0}</strong> schools.
              {processingResults.summary?.failed > 0 && (
                <> Failed: <strong>{processingResults.summary.failed}</strong> schools.</>
              )}
            </Alert>

            {/* Processed Schools */}
            {processingResults.processed?.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <CheckIcon color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Successfully Processed ({processingResults.processed.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>School UUID</TableCell>
                          <TableCell>Records Added</TableCell>
                          <TableCell>Years</TableCell>
                          <TableCell>Grades</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {processingResults.processed.map((school) => (
                          <TableRow key={school.uuid}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {school.uuid.substring(0, 8)}...
                            </TableCell>
                            <TableCell>{school.enrollment_records_added}</TableCell>
                            <TableCell>{school.data_years?.join(', ')}</TableCell>
                            <TableCell>{school.grades?.join(', ')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Failed Schools */}
            {processingResults.failed?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <ErrorIcon color="error" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Failed ({processingResults.failed.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>School UUID</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {processingResults.failed.map((school, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{school.uuid}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{school.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={resetWorkflow} startIcon={<RefreshIcon />}>
                Process More Schools
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Main Workflow (not shown when results are displayed) */}
      {!processingResults && (
        <>
          {/* Team Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TeamIcon />
                Step 1: Select Team
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the team whose drop-a-pin schools you want to upgrade. Schools in the Excel file 
                will be matched against this team's drop-a-pin locations.
              </Typography>

              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel>Select Team</InputLabel>
                <Select
                  value={selectedTeam}
                  onChange={(e) => {
                    setSelectedTeam(e.target.value);
                    setPreviewData(null);
                    setSelectedSchools(new Set());
                  }}
                  label="Select Team"
                  disabled={isLoadingTeams || isProcessing}
                >
                  {isLoadingTeams ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading teams...
                    </MenuItem>
                  ) : availableTeams.length > 0 ? (
                    availableTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name} ({team.school_count || 0} schools)
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No teams available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon />
                Step 2: Upload Excel File
              </Typography>

              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.main' : 'grey.400',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: isDragging ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => document.getElementById('excel-upload').click()}
              >
                <input
                  id="excel-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <UploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {selectedFile ? selectedFile.name : 'Drag & drop Excel file here, or click to select'}
                </Typography>
                {selectedFile && (
                  <Chip
                    label={`${(selectedFile.size / 1024).toFixed(1)} KB`}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {selectedTeam && selectedFile && !previewData && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={parseExcelFile}
                    disabled={isParsing}
                    startIcon={isParsing ? <CircularProgress size={20} /> : <SchoolIcon />}
                  >
                    {isParsing ? 'Parsing...' : 'Parse & Match Schools'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Preview Results */}
          {previewData && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon />
                  Step 3: Preview & Select Schools
                </Typography>

                {/* Summary chips */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<CheckIcon />}
                    label={`${previewData.matched_schools?.length || 0} Auto-Matched`}
                    color="success"
                  />
                  {Object.keys(manualMatches).length > 0 && (
                    <Chip
                      icon={<CheckIcon />}
                      label={`${Object.keys(manualMatches).length} Manual`}
                      color="info"
                    />
                  )}
                  <Chip
                    icon={<WarningIcon />}
                    label={`${Math.max(0, (previewData.unmatched_schools?.length || 0) - Object.keys(manualMatches).length)} Still Unmatched`}
                    color={(previewData.unmatched_schools?.length || 0) - Object.keys(manualMatches).length > 0 ? "warning" : "default"}
                  />
                  <Chip
                    label={`Years: ${previewData.summary?.years_found?.join(', ') || 'None'}`}
                    variant="outlined"
                  />
                  <Chip
                    label={`${previewData.available_drop_a_pins || 0} Drop-a-pins available`}
                    variant="outlined"
                  />
                </Box>

                {/* Parse errors */}
                {previewData.parse_errors?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Parsing Warnings</AlertTitle>
                    {previewData.parse_errors.map((err, idx) => (
                      <div key={idx}>{err.message}</div>
                    ))}
                  </Alert>
                )}

                {/* Matched Schools Table */}
                {previewData.matched_schools?.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        <CheckIcon color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Auto-Matched Schools ({previewData.matched_schools.length})
                        {selectedSchools.size > 0 && (
                          <Chip
                            size="small"
                            label={`${selectedSchools.size} selected`}
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedSchools.size === getAllMatchedSchools().length && getAllMatchedSchools().length > 0}
                              indeterminate={selectedSchools.size > 0 && selectedSchools.size < getAllMatchedSchools().length}
                              onChange={toggleAllSchools}
                            />
                          }
                          label="Select All (Auto + Manual)"
                        />
                      </Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedSchools.size === getAllMatchedSchools().length && getAllMatchedSchools().length > 0}
                                  indeterminate={selectedSchools.size > 0 && selectedSchools.size < getAllMatchedSchools().length}
                                  onChange={toggleAllSchools}
                                />
                              </TableCell>
                              <TableCell>School Name</TableCell>
                              <TableCell>Match Score</TableCell>
                              <TableCell>Enrollment Data</TableCell>
                              <TableCell>Preview</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {previewData.matched_schools.map((school) => {
                              const enrollmentTable = formatEnrollmentTable(school.enrollment_data);
                              const isExpanded = expandedEnrollment.has(school.uuid);
                              
                              return (
                                <React.Fragment key={school.uuid}>
                                  <TableRow 
                                    selected={selectedSchools.has(school.uuid)}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell padding="checkbox" onClick={() => toggleSchoolSelection(school.uuid)}>
                                      <Checkbox checked={selectedSchools.has(school.uuid)} />
                                    </TableCell>
                                    <TableCell onClick={() => toggleSchoolSelection(school.uuid)}>
                                      <Typography variant="body2" fontWeight="medium">
                                        {school.name}
                                      </Typography>
                                      {school.matched_name && school.matched_name !== school.name.toLowerCase() && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Matched to: {school.matched_name}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell onClick={() => toggleSchoolSelection(school.uuid)}>
                                      <Chip
                                        size="small"
                                        label={`${Math.round(school.match_score)}%`}
                                        color={school.match_score >= 90 ? 'success' : school.match_score >= 80 ? 'warning' : 'error'}
                                      />
                                    </TableCell>
                                    <TableCell onClick={() => toggleSchoolSelection(school.uuid)}>
                                      {formatEnrollmentSummary(school.enrollment_data)}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        variant={isExpanded ? "contained" : "outlined"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleEnrollmentPreview(school.uuid);
                                        }}
                                      >
                                        {isExpanded ? 'Hide' : 'Show'}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                  {/* Enrollment Preview Row */}
                                  {isExpanded && enrollmentTable && (
                                    <TableRow>
                                      <TableCell colSpan={5} sx={{ bgcolor: 'grey.50', p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                          Enrollment Data Preview for {school.name}
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Year</TableCell>
                                                {enrollmentTable.grades.map(grade => (
                                                  <TableCell key={grade} align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                                                    {grade}
                                                  </TableCell>
                                                ))}
                                                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Total</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {enrollmentTable.years.map(year => {
                                                const yearData = enrollmentTable.data[year];
                                                const yearTotal = Object.values(yearData).reduce((sum, val) => sum + (val || 0), 0);
                                                return (
                                                  <TableRow key={year}>
                                                    <TableCell sx={{ fontWeight: 'medium' }}>{year}</TableCell>
                                                    {enrollmentTable.grades.map(grade => (
                                                      <TableCell key={grade} align="center">
                                                        {yearData[grade] ?? '-'}
                                                      </TableCell>
                                                    ))}
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{yearTotal}</TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Unmatched Schools with Manual Matching */}
                {previewData.unmatched_schools?.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="warning" />
                        <Typography>
                          Unmatched Schools ({previewData.unmatched_schools.length})
                        </Typography>
                        {Object.keys(manualMatches).length > 0 && (
                          <Chip 
                            size="small" 
                            color="success" 
                            label={`${Object.keys(manualMatches).length} manually matched`} 
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        These schools from the Excel file could not be automatically matched.
                        Use the dropdown to manually select the correct drop-a-pin location for each school.
                      </Alert>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>School Name (from Excel)</TableCell>
                              <TableCell>Enrollment Data</TableCell>
                              <TableCell sx={{ minWidth: 250 }}>Match to Drop-a-Pin</TableCell>
                              <TableCell>Preview</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {previewData.unmatched_schools.map((school, idx) => {
                              const availableDropAPins = getAvailableDropAPins(school.name);
                              const currentMatch = manualMatches[school.name];
                              const enrollmentTable = formatEnrollmentTable(school.enrollment_data);
                              const isExpanded = currentMatch && expandedEnrollment.has(currentMatch.uuid);
                              
                              return (
                                <React.Fragment key={idx}>
                                  <TableRow sx={{ bgcolor: currentMatch ? 'success.lighter' : 'inherit' }}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {school.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        First seen: {school.sheet} ({school.year})
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {formatEnrollmentSummary(school.enrollment_data)}
                                    </TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small">
                                        <Select
                                          value={currentMatch?.uuid || ''}
                                          onChange={(e) => handleManualMatch(school.name, e.target.value, school.enrollment_data)}
                                          displayEmpty
                                        >
                                          <MenuItem value="">
                                            <em>-- Select a drop-a-pin --</em>
                                          </MenuItem>
                                          {availableDropAPins.map(dap => (
                                            <MenuItem key={dap.uuid} value={dap.uuid}>
                                              {dap.name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {currentMatch && (
                                        <Chip 
                                          size="small" 
                                          color="success" 
                                          label="Manual Match" 
                                          sx={{ mt: 0.5 }}
                                          onDelete={() => handleManualMatch(school.name, null, null)}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {enrollmentTable && (
                                        <Button
                                          size="small"
                                          variant={isExpanded ? "contained" : "outlined"}
                                          onClick={() => currentMatch && toggleEnrollmentPreview(currentMatch.uuid)}
                                          disabled={!currentMatch}
                                        >
                                          {isExpanded ? 'Hide' : 'Show'}
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  {/* Enrollment Preview Row */}
                                  {isExpanded && enrollmentTable && currentMatch && (
                                    <TableRow>
                                      <TableCell colSpan={4} sx={{ bgcolor: 'grey.50', p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                          Enrollment Preview for {school.name} → {currentMatch.name}
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Year</TableCell>
                                                {enrollmentTable.grades.map(grade => (
                                                  <TableCell key={grade} align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                                                    {grade}
                                                  </TableCell>
                                                ))}
                                                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Total</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {enrollmentTable.years.map(year => {
                                                const yearData = enrollmentTable.data[year];
                                                const yearTotal = Object.values(yearData).reduce((sum, val) => sum + (val || 0), 0);
                                                return (
                                                  <TableRow key={year}>
                                                    <TableCell sx={{ fontWeight: 'medium' }}>{year}</TableCell>
                                                    {enrollmentTable.grades.map(grade => (
                                                      <TableCell key={grade} align="center">
                                                        {yearData[grade] ?? '-'}
                                                      </TableCell>
                                                    ))}
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{yearTotal}</TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          )}

          {/* Processing Options & Action */}
          {previewData && selectedSchools.size > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProcessIcon />
                  Step 4: Process Schools
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={runProjections}
                          onChange={(e) => setRunProjections(e.target.checked)}
                        />
                      }
                      label="Run Projections (recommended)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={runMetrics}
                          onChange={(e) => setRunMetrics(e.target.checked)}
                        />
                      }
                      label="Run Metrics (recommended)"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Ready to upgrade <strong>{selectedSchools.size}</strong> school(s) in team{' '}
                  <strong>{getTeamName(selectedTeam)}</strong>.
                  This will add enrollment data and run projections/metrics processing.
                </Alert>

                {isProcessing && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      Processing schools... This may take a few minutes.
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={resetWorkflow}
                    disabled={isProcessing}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={processSelectedSchools}
                    disabled={isProcessing || selectedSchools.size === 0}
                    startIcon={isProcessing ? <CircularProgress size={20} /> : <ProcessIcon />}
                    size="large"
                  >
                    {isProcessing ? 'Processing...' : `Upgrade ${selectedSchools.size} School(s)`}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default UpgradeDropAPinProcessor;

